import { RoleNames } from 'core/auth/Account';
import * as parser from 'utils/ActorRoleParser';
import {
  ActorRolePermission,
  InverseActorPermission,
  PermissionItem,
  ActorRolePermissionDescriptor,
  AgencyAddOnPermission,
  ActorFuncPermission,
  Pmax3OrderPermission,
  InConditionPermission,
  PICActorPermission,
  HasAllActorRolePermission,
  InverseAgencyAddonPermission
} from './PermissionAware';
import { Permission } from 'core/auth/Permission';
import { L1Object } from 'core/l1Object/L1Object';
import { Order, State } from 'core/order/Order';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

function getRoleDescriptors(
  ...roleNames: RoleNames[]
): ActorRolePermissionDescriptor {
  const descriptors = roleNames.reduce<ActorRolePermissionDescriptor>(
    (partial, roleName) => {
      const type = parser.getRoleNamePart(roleName, 0, 'system').toLowerCase();
      const role = parser.getRoleNamePart(roleName, 1, 'admin').toLowerCase();
      const existing = partial[type];
      return {
        ...partial,
        [type]: existing ? [...existing, role] : [role]
      };
    },
    {}
  );
  return descriptors;
}

export function isRoles(...roleNames: RoleNames[]): PermissionItem {
  if (roleNames.length < 1) {
    throw new Error('role names can not be empty');
  }
  return new PermissionItem(
    new ActorRolePermission(getRoleDescriptors(...roleNames))
  );
}

export function notRoles(...roleNames: RoleNames[]): PermissionItem {
  if (roleNames.length < 1) {
    throw new Error('role names can not be empty');
  }
  return new PermissionItem(
    new InverseActorPermission(
      new ActorRolePermission(getRoleDescriptors(...roleNames))
    )
  );
}

export function hasTheseRoles(...roleNames: RoleNames[]): PermissionItem {
  if (roleNames.length < 1) {
    throw new Error('role names can not be empty');
  }
  return new PermissionItem(
    new HasAllActorRolePermission(getRoleDescriptors(...roleNames))
  );
}

export function notFuncs(...funcPermissions: Permission[]): PermissionItem {
  if (funcPermissions.length < 1) {
    throw new Error('function permissions can not be empty');
  }
  return new PermissionItem(
    new InverseActorPermission(new ActorFuncPermission(funcPermissions))
  );
}

export function hasFuncs(...funcPermissions: Permission[]): PermissionItem {
  if (funcPermissions.length < 1) {
    throw new Error('function permissions can not be empty');
  }
  return new PermissionItem(new ActorFuncPermission(funcPermissions));
}

export function addOnEnabled(...keys: string[]): PermissionItem {
  if (keys.length < 1) {
    throw new Error('role names can not be empty');
  }
  return new PermissionItem(null, new AgencyAddOnPermission(keys));
}

export function addOnDisabled(...keys: string[]): PermissionItem {
  if (keys.length < 1) {
    throw new Error('role names can not be empty');
  }
  return new PermissionItem(
    null,
    new InverseAgencyAddonPermission(new AgencyAddOnPermission(keys))
  );
}

export function isPmax3Order(orderId: number): PermissionItem {
  return new PermissionItem(null, null).and(new Pmax3OrderPermission(orderId));
}

export function inCondition(condition: () => boolean): PermissionItem {
  return new PermissionItem(null, null).and(
    new InConditionPermission(condition)
  );
}

export function isSelfServeAdObject(
  adObject: L1Object | undefined
): PermissionItem {
  return new PermissionItem(null, null).and(
    new InConditionPermission(() => (adObject ? !!adObject.isSelfServe : false))
  );
}

export function notSelfServeAdObject(
  adObject: L1Object | undefined
): PermissionItem {
  return new PermissionItem(null, null).and(
    new InConditionPermission(() => (adObject ? !adObject.isSelfServe : true))
  );
}

export function notSettleOrder(order: Order | undefined): PermissionItem {
  return new PermissionItem(null, null).and(
    new InConditionPermission(() =>
      order ? ![State.SETTLE, State.SETTLED].includes(order.state) : false
    )
  );
}

/**
 *「停用」改為合併判定（RMax 已封存 OR PMax 自停），
 * 值由後端算在 `effectiveArchived` 上。
 *
 * 判定完全在前端推導 —— `ChannelGroupSetting` registry 現在包含停用渠道，
 * 所以 `channelGroupId` 一路查得到狀態，後端不需要另外送唯讀旗標。
 *   - 群組 / 活動：直接給 `l1Object.channelGroupId`
 *   - 素材：先用 `ChannelGroupSetting.findChannelGroupIdByCreativeType(creativeType)`
 *     反查出 channelGroupId 再傳進來
 *
 * 形狀刻意比照 `notSettleOrder`：走 `InConditionPermission` 而非 addon feature 判定。
 * `AddonFeatureManager.isFeatureEnable` 對 system admin 永遠回 false，
 * 寫成 addon 會讓 system admin 的唯讀行為反直覺。
 *
 * ⚠️ 查不到渠道時（`undefined`、registry 尚未載入、渠道已被刪除）視為**未停用**。
 * 前端 fail-open 是刻意的：寫入面後端另有擋（前後端都要擋），
 * 而 fail-closed 會在 registry 還沒載入的空窗把所有畫面變成唯讀。
 */
export function notArchivedChannel(
  channelGroupId: number | undefined
): PermissionItem {
  return new PermissionItem(null, null).and(
    new InConditionPermission(
      () => !ChannelGroupSetting.isEffectivelyArchivedChannel(channelGroupId)
    )
  );
}

export function notPICActor() {
  return new PermissionItem(
    new InverseActorPermission(new PICActorPermission())
  );
}
